// Admin "Numbers" tab: the same location-grouped people & call-queue list users
// see, with a site selector so an admin can curate per audience. The dropdown
// picks the hide SCOPE:
//   • "All sites"  → hiding removes the entry for everyone (tenant-wide).
//   • a specific site → hiding removes the entry only for VIEWERS of that site,
//     so a shared call queue can be hidden at one site and shown at another.
// Hiding/unhiding reuses the eye-off control from the user-side Numbers tab.
// Per-site state is read straight from the hidden config; tenant-wide hides are
// managed under "All sites" (this tab notes that so the two scopes stay clear).

import { useMemo, useState, type ReactNode } from 'react';
import { MapPin, Globe } from 'lucide-react';
import { NumbersByLocation, type NumberPerson } from './NumbersByLocation';
import { api, type NumbersLocationAccess } from '../api/client';
import { useApi } from '../hooks/useApi';

interface NumbersAdminProps {
  people: NumberPerson[];
  queues: NumberPerson[];
  /** Per-site hides from the tenant hidden config (slug → object ids). */
  siteHiddenIds: Record<string, string[]>;
  /** Tenant-wide friendly-name overrides (object id → name). */
  tenantNames: Record<string, string>;
  /** Per-site friendly-name overrides (slug → { object id → name }). */
  siteNames: Record<string, Record<string, string>>;
  accentColor: string;
  onDial: (p: NumberPerson) => void;
  renderCall: (p: NumberPerson) => ReactNode;
  /** Tenant-wide ("All sites") hide/unhide — same handlers as the user tab. */
  onHideAll: (p: NumberPerson) => void;
  onUnhideAll: (p: NumberPerson) => void;
  /** Per-site hide/unhide for the selected location slug. */
  onHideSite: (slug: string, p: NumberPerson) => void;
  onUnhideSite: (slug: string, p: NumberPerson) => void;
  /** Rename for end users — tenant-wide (All sites) or per the selected site.
   *  Pass null to clear the override. */
  onRenameAll: (p: NumberPerson, name: string | null) => void;
  onRenameSite: (slug: string, p: NumberPerson, name: string | null) => void;
}

export function NumbersAdmin({
  people, queues, siteHiddenIds, tenantNames, siteNames, accentColor, onDial, renderCall,
  onHideAll, onUnhideAll, onHideSite, onUnhideSite, onRenameAll, onRenameSite,
}: NumbersAdminProps) {
  // Own poll so it only runs while this tab is mounted. Locations define the
  // selectable sites; the list relabels each entry's office to a location name
  // server-side, so we match site membership on that name.
  const { data: accessData } = useApi(() => api.listNumbersAccess(), { pollMs: 30000 });
  const sites = useMemo<NumbersLocationAccess[]>(
    () => [...(accessData || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [accessData]
  );

  const [scope, setScope] = useState<string>('all'); // 'all' or a location slug
  // Reset to All sites if the selected site disappears (deleted in Locations).
  const selectedSite = scope === 'all' ? null : sites.find(s => s.id === scope) || null;
  const activeScope = scope === 'all' ? 'all' : selectedSite ? scope : 'all';

  const { shownPeople, shownQueues, hideHandler, unhideHandler, renameHandler } = useMemo(() => {
    if (activeScope === 'all') {
      // Tenant-wide scope: hidden flags come from the server; overlay the
      // tenant-wide friendly override so admins see/edit it (server sends the
      // original displayName to admins).
      const overlay = (e: NumberPerson): NumberPerson => ({ ...e, friendlyName: tenantNames[e.id.toLowerCase()] });
      return {
        shownPeople: people.map(overlay),
        shownQueues: queues.map(overlay),
        hideHandler: onHideAll,
        unhideHandler: onUnhideAll,
        renameHandler: onRenameAll,
      };
    }
    const hiddenSet = new Set((siteHiddenIds[activeScope] || []).map(x => x.toLowerCase()));
    const nameMap = siteNames[activeScope] || {};
    const flag = (e: NumberPerson): NumberPerson => {
      const id = e.id.toLowerCase();
      const siteHidden = hiddenSet.has(id);
      return {
        ...e,
        // Keep the server's tenant-wide hide visible in this scope (it still hides
        // the entry for everyone) — OR it with the per-site hide, don't clobber it.
        hidden: !!e.hidden || siteHidden,
        hiddenReason: siteHidden ? 'explicit' : e.hiddenReason,
        // The per-site override is editable here; the tenant-wide name is shown as
        // an inherited fallback so the preview matches what site viewers see.
        friendlyName: nameMap[id],
        inheritedName: tenantNames[id],
      };
    };
    // People grouped under this exact location slug (server-resolved), plus shared
    // queues (no slug) — both are seen by this site's viewers, so both are
    // hideable/renameable here. Matching on slug (not display name) stays aligned
    // with the server's per-site enforcement under overlapping office strings.
    const sp = people.filter(p => p.ownSlug === activeScope).map(flag);
    const sq = queues.filter(q => !q.ownSlug || q.ownSlug === activeScope).map(flag);
    return {
      shownPeople: sp,
      shownQueues: sq,
      hideHandler: (p: NumberPerson) => onHideSite(activeScope, p),
      unhideHandler: (p: NumberPerson) => onUnhideSite(activeScope, p),
      renameHandler: (p: NumberPerson, name: string | null) => onRenameSite(activeScope, p, name),
    };
  }, [activeScope, selectedSite, people, queues, siteHiddenIds, tenantNames, siteNames,
      onHideAll, onUnhideAll, onHideSite, onUnhideSite, onRenameAll, onRenameSite]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <label className="block text-xs font-medium text-gray-700 mb-1.5">Curate numbers for</label>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select value={scope} onChange={e => setScope(e.target.value)}
              className="appearance-none pl-9 pr-8 py-2 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 min-w-[220px]">
              <option value="all">All sites (everyone)</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {activeScope === 'all' ? <Globe className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
            </span>
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-[10px]">▼</span>
          </div>
        </div>
        <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
          {activeScope === 'all'
            ? 'Hiding or renaming an entry applies to everyone, tenant-wide. The original Entra name is kept — end users just see the name you set.'
            : <>Hiding or renaming an entry applies only to people who view <span className="font-medium">{selectedSite!.name}</span> (their office location is there, or they’re in its viewer groups). The same entry can read differently — or stay hidden — at other sites. The original Entra name is always kept; tenant-wide changes live under <span className="font-medium">All sites</span>.</>}
        </p>
        {sites.length === 0 && (
          <p className="text-[11px] text-amber-600 mt-1.5">
            No locations configured yet — add them in the Locations tab to hide numbers per site.
          </p>
        )}
      </div>

      <NumbersByLocation
        key={activeScope}
        people={shownPeople}
        queues={shownQueues}
        isAdmin
        onHide={hideHandler}
        onUnhide={unhideHandler}
        onRename={renameHandler}
        onDial={onDial}
        renderCall={renderCall}
      />
    </div>
  );
}
